(() => {
  if (window.PMBLeadEmail?.initialized) return;

  const PMBLeadEmail = {
    initialized: true,
    states: new WeakMap()
  };
  window.PMBLeadEmail = PMBLeadEmail;

  const FORM_SELECTOR = '[data-pmb-lead-form]';
  const SEGMENT_TAGS = new Set(['lead_general', 'lead_fashion', 'lead_jewellery']);

  const waitForTurnstile = async () => {
    for (let attempt = 0; attempt < 80; attempt += 1) {
      if (window.turnstile?.render) return window.turnstile;
      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }
    throw new Error('Secure verification could not load. Please disable blocking for this page and try again.');
  };

  const getRoot = (form) => form.closest('[data-pmb-email-root]');

  const categoryFor = (form) => {
    const selected = form.querySelector('input[name="lead_category"]:checked')?.value;
    if (selected === 'jewellery' || selected === 'fashion') return selected;
    const tags = form.querySelector('input[name="contact[tags]"]')?.value || '';
    return tags.includes('lead_jewellery') ? 'jewellery' : 'fashion';
  };

  const syncCategory = (form) => {
    const root = getRoot(form);
    const tags = form.querySelector('input[name="contact[tags]"]');
    const category = categoryFor(form);
    if (tags) {
      const staticTags = tags.value.split(',').map((tag) => tag.trim()).filter((tag) => tag && !SEGMENT_TAGS.has(tag));
      tags.value = [...new Set([...staticTags, `lead_${category}`])].join(',');
    }

    const download = form.querySelector('[data-lead-download]');
    if (download && root) {
      const jewellery = category === 'jewellery';
      download.href = jewellery ? root.dataset.jewelleryDownload : root.dataset.fashionDownload;
      download.textContent = jewellery ? 'Download the Jewellery starter pack →' : 'Download the Fashion starter pack →';
    }
    return category;
  };

  const ensureWidget = async (form) => {
    const existing = PMBLeadEmail.states.get(form);
    if (existing?.widgetId !== undefined) return existing;

    const root = getRoot(form);
    const container = form.querySelector('[data-pmb-turnstile]');
    const sitekey = root?.dataset.pmbTurnstileSiteKey;
    if (!root || !container || !sitekey) throw new Error('Secure email delivery is not configured on this form.');

    const turnstile = await waitForTurnstile();
    const state = { widgetId: undefined, token: '', resolve: null, reject: null, timer: null };
    state.widgetId = turnstile.render(container, {
      sitekey,
      action: 'lead_email',
      appearance: 'interaction-only',
      execution: 'execute',
      size: 'flexible',
      callback: (token) => {
        state.token = token;
        if (state.timer) window.clearTimeout(state.timer);
        state.resolve?.(token);
        state.resolve = null;
        state.reject = null;
      },
      'expired-callback': () => {
        state.token = '';
      },
      'error-callback': () => {
        if (state.timer) window.clearTimeout(state.timer);
        state.reject?.(new Error('Secure verification failed. Please try again.'));
        state.resolve = null;
        state.reject = null;
      }
    });
    PMBLeadEmail.states.set(form, state);
    return state;
  };

  const getVerificationToken = async (form) => {
    const state = await ensureWidget(form);
    if (state.token) return state.token;

    return new Promise((resolve, reject) => {
      state.resolve = resolve;
      state.reject = reject;
      state.timer = window.setTimeout(() => {
        state.resolve = null;
        state.reject = null;
        reject(new Error('Secure verification timed out. Please try again.'));
      }, 15000);
      window.turnstile.execute(state.widgetId);
    });
  };

  const resetVerification = (form) => {
    const state = PMBLeadEmail.states.get(form);
    if (!state || state.widgetId === undefined || !window.turnstile) return;
    state.token = '';
    state.resolve = null;
    state.reject = null;
    if (state.timer) window.clearTimeout(state.timer);
    window.turnstile.reset(state.widgetId);
  };

  const showFormError = (form, message) => {
    const fields = form.querySelector('[data-lead-form-fields]');
    let errors = form.querySelector('.lead-capture__errors, .site-footer__newsletter-errors');
    if (!errors) {
      errors = document.createElement('p');
      errors.className = form.id === 'FooterNewsletterForm' ? 'site-footer__newsletter-errors' : 'lead-capture__errors';
      errors.setAttribute('role', 'alert');
      fields?.append(errors);
    }
    errors.textContent = message;
  };

  const confirmShopifySubscription = async (form) => {
    const response = await fetch(form.action, {
      method: (form.method || 'post').toUpperCase(),
      body: new FormData(form),
      headers: { Accept: 'text/html' }
    });
    const responseHtml = await response.text();
    const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
    const responseForm = responseDocument.getElementById(form.id);
    const confirmed = responseForm?.querySelector('[data-lead-success]:not([hidden])');
    const serverErrors = responseForm?.querySelector('.lead-capture__errors, .site-footer__newsletter-errors')?.textContent?.trim();
    if (!response.ok || !confirmed) {
      throw new Error(serverErrors || 'Shopify could not confirm this subscription. Please check the email and try again.');
    }
  };

  const sendStarterEmail = async (form, email, category, turnstileToken) => {
    const root = getRoot(form);
    const endpoint = root?.dataset.pmbWorkerUrl;
    if (!endpoint) throw new Error('Starter email delivery is not configured.');

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, category, turnstileToken })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok) throw new Error(result.code || 'delivery_failed');
    return result;
  };

  const bindForm = (form) => {
    if (form.dataset.pmbBound === 'true') return;
    form.dataset.pmbBound = 'true';

    form.querySelectorAll('input[name="lead_category"]').forEach((input) => {
      input.addEventListener('change', () => syncCategory(form));
    });
    syncCategory(form);
    ensureWidget(form).catch(() => {});

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const fields = form.querySelector('[data-lead-form-fields]');
      const success = form.querySelector('[data-lead-success]');
      const deliveryStatus = form.querySelector('[data-pmb-delivery-status]');
      const button = form.querySelector('button[type="submit"]');
      const originalLabel = button?.textContent || '';
      const email = form.querySelector('input[name="contact[email]"]')?.value.trim();
      const category = syncCategory(form);
      let shopifySaved = false;

      form.setAttribute('aria-busy', 'true');
      if (button) {
        button.disabled = true;
        button.textContent = 'Sending…';
      }

      try {
        const turnstileToken = await getVerificationToken(form);
        await confirmShopifySubscription(form);
        shopifySaved = true;

        if (fields) fields.hidden = true;
        if (success) {
          success.hidden = false;
          success.focus({ preventScroll: true });
        }
        if (deliveryStatus) deliveryStatus.textContent = 'Your subscription is saved. We are sending your starter sample now; you can also download it below.';

        const delivery = await sendStarterEmail(form, email, category, turnstileToken);
        if (deliveryStatus) {
          deliveryStatus.textContent = delivery.alreadySent
            ? 'Your starter sample was already emailed. Check your inbox or spam folder; you can also download it below.'
            : 'Email sent—check your inbox. If it is not visible in a few minutes, check spam; you can also download it below.';
        }

        form.classList.add('is-success');
        window.pmbTrack?.('pmb_email_subscribe', {
          lead_segment: `lead_${category}`,
          source: form.dataset.pmbSource || 'unknown'
        });
      } catch (error) {
        if (shopifySaved) {
          if (fields) fields.hidden = true;
          if (success) success.hidden = false;
          if (deliveryStatus) deliveryStatus.textContent = 'Your subscription is saved, but the email could not be delivered right now. Download your starter sample below and keep START15 for checkout.';
        } else {
          showFormError(form, error?.message || 'We could not save your subscription. Please check your connection and try again.');
          if (button) {
            button.disabled = false;
            button.textContent = originalLabel;
          }
        }
      } finally {
        resetVerification(form);
        form.removeAttribute('aria-busy');
      }
    });
  };

  const initialize = (root = document) => root.querySelectorAll(FORM_SELECTOR).forEach(bindForm);
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
